import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    FaBriefcase,
} from 'react-icons/fa';
import {
    HiUserGroup,
} from 'react-icons/hi';
import {
    BsGlobe,
} from 'react-icons/bs';
import {
    MdOutlineEvent,
} from 'react-icons/md';
import {
    BiNews
} from 'react-icons/bi';
import {
    GoSponsorTiers
} from 'react-icons/go';
import {
    RxDashboard
} from 'react-icons/rx';
import { IoIosNotifications } from "react-icons/io";
import { LuHeartHandshake, LuMessageSquare } from 'react-icons/lu';
import { GrGallery, GrUserAdmin } from "react-icons/gr";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from 'react';
import { updateMember } from "../../store/membersSlice";
import { Ban, FormInput, LogOut, Settings, User } from 'lucide-react';
import { useCookies } from 'react-cookie';

const LeftSidebar = ({ onNavigate, isMobile = false, isExpanded = false }) => {
    const profile = useSelector((state) => state.profile);
    const dispatch = useDispatch();
    const location = useLocation();
    const [hovered, setHovered] = useState(false);
    const allRolesAccess = useSelector((state) => state?.roles?.data?.role);
    const portalInfo = useSelector((state) => state.portalInfo.data);
    const portalType = portalInfo?.portalType || 'Community';
        const [cookie, setCookie, removeCookie] = useCookies('token');
    
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/alumni/all`);
                if (response.ok) {
                    const membersData = await response.json();
                    dispatch(updateMember(membersData));
                } else {
                    throw new Error('Failed to fetch members');
                }
            } catch (error) {
                console.error('Error fetching members:', error);
            }
        };

        // fetchMembers(); // Enable if needed
    }, [dispatch]);

    const groupAdmin = [0, 1].includes(profile.profileLevel) || allRolesAccess?.groups.all
    const profanityAdmin = [0, 1].includes(profile.profileLevel) || allRolesAccess?.profanity.all
    const manageRolesAdmin = [0, 1].includes(profile.profileLevel) || allRolesAccess?.fullAdmin.all

    // Function to get regular sidebar items based on portal type
    const getItemsForPortalType = (portalType, groupAdmin) => {
        const baseItems = {
            feeds: { path: '/home', label: 'Feeds', icon: <RxDashboard /> },
            profile: { path: '/home/profile', label: 'Profile', icon: <User size={20} /> },
            members: { path: '/home/members', label: 'Members', icon: <BsGlobe /> },
            groups: {
                path: groupAdmin ? '/home/groups' : '/home/groups/suggested-groups',
                label: 'Groups',
                icon: <HiUserGroup />
            },
            chat: { path: '/home/chatv2', label: 'Chat', icon: <LuMessageSquare /> },
            news: { path: '/home/news', label: 'News', icon: <BiNews /> },
            businessConnect: { 
                path: '/home/business-connect', 
                label: 'Business Connect', 
                icon: <LuHeartHandshake /> 
            },
            sponsorships: { 
                path: '/home/sponsorship-connect', 
                label: 'Sponsorships', 
                icon: <GoSponsorTiers size={22} /> 
            },
            events: { path: '/home/events', label: 'Events', icon: <MdOutlineEvent /> },
            jobs: { path: '/home/jobs', label: 'Jobs/Internships', icon: <FaBriefcase /> },
            gallery: { path: '/home/photo-gallery', label: 'Photo Gallery', icon: <GrGallery /> },
            notifications: { 
                path: '/home/notifications', 
                label: 'Notifications', 
                icon: <IoIosNotifications size={22} /> 
            },
            settings: { 
                path: '/home/profile/profile-settings', 
                label: 'Profile Settings', 
                icon: <Settings size={19} /> 
            },
            forum: { 
                path: '/home/forum', 
                label: 'Forums', 
                icon: <FormInput size={19} /> 
            },
        };

        switch (portalType) {
            case 'Community':
                return [
                    baseItems.feeds,
                    baseItems.profile,
                    baseItems.members,
                    baseItems.groups,
                    baseItems.forum,
                    baseItems.news,
                    baseItems.events,
                    baseItems.chat,
                    baseItems.gallery,
                    baseItems.notifications,
                ];

            case 'School':
                return [
                    baseItems.feeds,
                    baseItems.profile,
                    baseItems.members,
                    baseItems.groups,
                    baseItems.events,
                    baseItems.chat,
                    baseItems.jobs,
                    baseItems.gallery,
                    baseItems.notifications,
                    // baseItems.settings
                ];

            case 'University':
                return [
                    baseItems.feeds,
                    baseItems.profile,
                    baseItems.members,
                    baseItems.groups,
                    baseItems.forum,
                    baseItems.chat,
                    baseItems.news,
                    baseItems.businessConnect,
                    baseItems.sponsorships,
                    baseItems.events,
                    baseItems.jobs,
                    baseItems.gallery,
                    baseItems.notifications,
                    // baseItems.settings
                ];

            default:
                // Fallback - show all items
                return Object.values(baseItems);
        }
    };

    // Function to get admin items based on portal type and permissions
    const getAdminItemsForPortalType = (portalType, permissions) => {
        const { manageRolesAdmin, profanityAdmin } = permissions;
        
        const baseAdminItems = {
            userVerification: {
                path: '/home/admin/user-verification',
                label: 'User Verification',
                icon: <GrUserAdmin />,
                isAdmin: manageRolesAdmin
            },
            prohibitedKeywords: {
                path: '/home/prohibited-keywords',
                label: 'Prohibited Keywords',
                icon: <Ban size={19} />,
                isAdmin: profanityAdmin
            },
            manageRoles: {
                path: '/home/manage-roles',
                label: 'Manage Roles',
                icon: <GrUserAdmin size={19} />,
                isAdmin: manageRolesAdmin
            },
         
        };

        switch (portalType) {
            case 'Community':
                return [
                    // baseAdminItems.userVerification,
                    baseAdminItems.prohibitedKeywords,
                    baseAdminItems.manageRoles
                ].filter(item => item.isAdmin);

            case 'School':
                return [
                    baseAdminItems.userVerification,
                    baseAdminItems.manageRoles
                ].filter(item => item.isAdmin);

            case 'University':
                return [
                    baseAdminItems.userVerification,
                    baseAdminItems.manageRoles
                ].filter(item => item.isAdmin);

            default:
                // Fallback - show basic admin items
                return [
                    baseAdminItems.userVerification,
                    baseAdminItems.manageRoles
                ].filter(item => item.isAdmin);
        }
    };

    // Get filtered items based on portal type
    const sidebarItems = getItemsForPortalType(portalType, groupAdmin);
    
    // Get filtered admin items based on portal type and permissions
    const adminItems = getAdminItemsForPortalType(portalType, {
        manageRolesAdmin,
        profanityAdmin
    });

    const isActive = (path) => {
        if (path === '/home') {
            return location.pathname === '/home'; // exact match only
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const handleNavigation = () => {
        if (onNavigate) {
            onNavigate();
        }
    };
  const navigate = useNavigate();
    const handleLogout = ()=>{
           // console.log('logout', cookie.token)
        removeCookie('token');
        navigate('/login');
         window.location.href = "/login";
    }

    // Determine if sidebar should show full content
    // Mobile: always show full if expanded, Desktop: show full only on hover
    const showFullContent = isMobile ? isExpanded : hovered;

    return (
        <div
            className={`h-full dynamic-site-bg-2 text-white flex flex-col transition-all duration-300 ease-in-out ${
                isMobile
                    ? 'w-72'
                    : showFullContent
                    ? 'w-[260px]'
                    : 'w-20'
            }`}
            onMouseEnter={() => !isMobile && setHovered(true)}
            onMouseLeave={() => !isMobile && setHovered(false)}
        >
            {/* Logo Section */}
            <div className={`text-center transition-all duration-300 ${
                showFullContent ? 'p-3' : 'p-3'
            }`}>
                <Link
                    to="/home"
                    className="block"
                    onClick={handleNavigation}
                >
                    {showFullContent ? (
                        <div className='h-[70px]'>
                            <img 
                                src="/v2/logo2.png" 
                                alt="InsideOut Logo" 
                                className="w-[200px] md:hidden h-[99px] mx-auto rounded-lg object-contain transition-all duration-300" 
                            />
                        </div>
                    ) : (
                        <div className='h-[70px]'>
                        </div>
                    )}
                </Link>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 px-3 py-4 overflow-y-auto custom-scrollbar">
                <nav className="space-y-1">
                    {/* Regular Sidebar Items */}
                    {sidebarItems.map((item, idx) => (
                        <div key={idx} className="relative group">
                            <Link
                                to={item.path}
                                onClick={handleNavigation}
                                className={`
                                    flex items-center rounded-lg transition-all duration-200 group/item relative
                                    ${showFullContent ? 'px-4 py-3' : 'px-2 py-3 justify-center'}
                                    ${isActive(item.path)
                                        ? 'dynamic-site-bg-3 text-white shadow-lg scale-[1.02] font-medium'
                                        : 'text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-1'
                                    }
                                `}
                            >
                                <span className={`text-lg flex-shrink-0 transition-transform duration-200 ${
                                    isActive(item.path) ? 'scale-110' : 'group-hover/item:scale-105'
                                } ${showFullContent ? 'mr-4' : ''}`}>
                                    {item.icon}
                                </span>

                                {showFullContent && (
                                    <>
                                        <span className="font-medium truncate text-sm">
                                            {item.label}
                                        </span>
                                        {isActive(item.path) && (
                                            <div className="ml-auto w-1 h-6 bg-white rounded-full"></div>
                                        )}
                                    </>
                                )}
                            </Link>

                            {/* Tooltip for collapsed state - only show on desktop when not expanded */}
                            {!isMobile && !showFullContent && (
                                <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                    {item.label}
                                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Admin Items Section */}
                    {adminItems.length > 0 && (
                        <>
                            <div className="border-t border-white/10 my-3"></div>
                            {adminItems.map((item, idx) => (
                                <div key={`admin-${idx}`} className="relative group">
                                    <Link
                                        to={item.path}
                                        onClick={handleNavigation}
                                        className={`
                                            flex items-center rounded-lg transition-all duration-200 group/item relative
                                            ${showFullContent ? 'px-4 py-3' : 'px-2 py-3 justify-center'}
                                            ${isActive(item.path)
                                                ? 'dynamic-site-bg-3 text-white shadow-lg scale-[1.02] font-medium'
                                                : 'text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-1'
                                            }
                                        `}
                                    >
                                        <span className={`text-lg flex-shrink-0 transition-transform duration-200 ${
                                            isActive(item.path) ? 'scale-110' : 'group-hover/item:scale-105'
                                        } ${showFullContent ? 'mr-4' : ''}`}>
                                            {item.icon}
                                        </span>

                                        {showFullContent && (
                                            <>
                                                <span className="font-medium truncate text-sm">
                                                    {item.label}
                                                </span>
                                                {isActive(item.path) && (
                                                    <div className="ml-auto w-1 h-6 bg-white rounded-full"></div>
                                                )}
                                            </>
                                        )}
                                    </Link>

                                    {/* Tooltip for collapsed state */}
                                    {!isMobile && !showFullContent && (
                                        <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                            {item.label}
                                            <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {/* Logout Option */}
                    <div className="relative group  md:hidden">
                        <button
                            onClick={handleLogout}
                            className={`
                                flex items-center rounded-lg transition-all duration-200 group/item relative
                                px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-1
                            `}
                        >
                            <span className="text-lg flex-shrink-0 transition-transform duration-200">
                                <LogOut size={16} className="mr-4" />
                            </span>
                            <span className="font-medium truncate text-sm">Logout</span>
                        </button>
                    </div>
                </nav>
            </div>

            {/* Footer Section - Only show when expanded */}
            {showFullContent && (
                <div className="p-4 border-t border-white/10">
                    <div className="text-center">
                        <p className="text-xs text-white/60">
                            © 2025 InsideOut Platform
                        </p>
                    </div>
                </div>
            )}

            {/* Custom Scrollbar Styles */}
            <style jsx>{`
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }

                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 2px;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
            `}</style>
        </div>
    );
};

export default LeftSidebar;
